import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../utils/helpers';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'blue' | 'green' | 'amber' | 'red' | 'cyan' | 'purple' | 'orange';
  className?: string;
  isLoading?: boolean;
}

const variantStyles = {
  default: 'bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 border-slate-200 dark:border-slate-800',
  blue: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800',
  green: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800',
  amber: 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-amber-200 dark:border-amber-800',
  red: 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800',
  cyan: 'bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-800 dark:to-cyan-700 border-cyan-200 dark:border-cyan-600',
  purple: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800',
  orange: 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border-orange-200 dark:border-orange-800',
};

const titleStyles = {
  default: 'text-slate-700 dark:text-slate-300',
  blue: 'text-blue-700 dark:text-blue-300',
  green: 'text-green-700 dark:text-green-300',
  amber: 'text-amber-700 dark:text-amber-300',
  red: 'text-red-700 dark:text-red-300',
  cyan: 'text-cyan-700 dark:text-cyan-200',
  purple: 'text-purple-700 dark:text-purple-300',
  orange: 'text-orange-700 dark:text-orange-300',
};

const valueStyles = {
  default: 'text-slate-800 dark:text-slate-200',
  blue: 'text-blue-800 dark:text-blue-200',
  green: 'text-green-800 dark:text-green-200',
  amber: 'text-amber-800 dark:text-amber-200',
  red: 'text-red-800 dark:text-red-200',
  cyan: 'text-cyan-800 dark:text-cyan-100',
  purple: 'text-purple-800 dark:text-purple-200',
  orange: 'text-orange-800 dark:text-orange-200',
};

const descriptionStyles = {
  default: 'text-slate-600 dark:text-slate-400',
  blue: 'text-blue-600 dark:text-blue-400',
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
  cyan: 'text-cyan-600 dark:text-cyan-300',
  purple: 'text-purple-600 dark:text-purple-400',
  orange: 'text-orange-600 dark:text-orange-400',
};

export function StatCard({
  title,
  value,
  description,
  icon,
  variant = 'default',
  className,
  isLoading = false,
}: StatCardProps) {
  return (
    <Card className={cn(variantStyles[variant], className)}>
      <CardHeader className="pb-2">
        <CardTitle className={cn('text-sm font-medium flex items-center gap-2', titleStyles[variant])}>
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn('text-3xl font-bold', valueStyles[variant])}>
          {isLoading ? '...' : value}
        </div>
        {description && (
          <p className={cn('text-xs mt-1', descriptionStyles[variant])}>
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
